import React from 'react';
import { Box } from '@mui/material';
import { Link } from 'react-router-dom';
import {
  signalBorders,
  signalFonts,
  signalRadii,
  signalShadows,
  signalStatus,
  signalSurfaces,
  signalText,
} from '@/theme/foundations/tokens';
import { Mono, StatusPill } from '@/common/components/signal';
import { signalScreens } from './screens/registry';

/** Gallery index for the seventeen Signal surfaces. */
export const SignalIndexPage: React.FC = () => {
  const built = signalScreens.filter((s) => s.Component).length;

  return (
    <Box
      sx={{
        padding: '48px 52px 100px',
        display: 'flex',
        flexDirection: 'column',
        gap: '40px',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <Mono
          size={11}
          weight={600}
          letterSpacing="0.14em"
          color={signalStatus.syncing.fg}
        >
          STUDIOVAULT · SIGNAL SYSTEM · SURFACES
        </Mono>
        <Box
          component="h1"
          sx={{
            margin: 0,
            fontFamily: signalFonts.display,
            fontWeight: 800,
            fontSize: '46px',
            lineHeight: 1,
            letterSpacing: '-0.035em',
          }}
        >
          Ten surfaces, one system
        </Box>
        <Mono size={11} color={signalText.muted}>
          {built} OF {signalScreens.length} BUILT
        </Mono>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: '16px',
        }}
      >
        {signalScreens.map((screen) => (
          <Box
            key={screen.id}
            component={Link}
            to={`/signal/${screen.id}`}
            sx={{
              textDecoration: 'none',
              color: 'inherit',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              padding: '18px',
              borderRadius: `${signalRadii['2xl']}px`,
              border: `1px solid ${signalBorders.default}`,
              background: signalSurfaces.cardGradient,
              boxShadow: signalShadows.card,
              transition: 'border-color 120ms ease',
              '&:hover': { borderColor: signalBorders.accentHover },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Mono size={11} weight={600} color={signalStatus.syncing.fg}>
                {screen.index}
              </Mono>
              <Box
                sx={{
                  flex: 1,
                  fontFamily: signalFonts.display,
                  fontWeight: 700,
                  fontSize: '17px',
                  letterSpacing: '-0.015em',
                }}
              >
                {screen.title}
              </Box>
              {screen.Component ? (
                <StatusPill status="synced" size={9} dot>
                  BUILT
                </StatusPill>
              ) : (
                <StatusPill status="neutral" size={9}>
                  TODO
                </StatusPill>
              )}
            </Box>

            <Box
              sx={{
                fontSize: '12.5px',
                lineHeight: 1.55,
                color: signalText.secondary,
              }}
            >
              {screen.summary}
            </Box>

            {screen.note && (
              <Mono size={10} color={signalText.faint}>
                {screen.note}
              </Mono>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default SignalIndexPage;
